import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Copy, Church } from "lucide-react";
import { toast } from "@/lib/toast";
import {
  getChurches,
  getChurchEvents,
  getFieldEvents,
  deleteChurchEvent,
  createChurchEvent,
} from "@/lib/api";
import { useConfirm } from "@/components/ConfirmDialog";
import { useCustomLists } from "@/lib/custom-lists";
import CenteredModal from "@/components/shared/CenteredModal";
import InlinePicker from "@/components/shared/InlinePicker";
import { sortByEventDate, isEventDone } from "../utils/agendaDates";
import { getLocalDateString } from "@/lib/date";
import AgendaEventCard from "./AgendaEventCard";
import AgendaTimeline from "./AgendaTimeline";

function normalizeQ(s) {
  return (s || "").toLowerCase().normalize("NFD").replace(/\p{M}/gu, "");
}

export default function LocalAgenda({ districtId, defaultChurchId, lockedChurchId, searchQuery = "" }) {
  const [churches, setChurches]           = useState([]);
  const [loadingChurches, setLoadingChurches] = useState(true);
  const [selectedChurch, setSelectedChurch]   = useState(defaultChurchId || "");
  const [events, setEvents]               = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [fieldPool, setFieldPool]         = useState([]);
  const [copySheetOpen, setCopySheetOpen] = useState(false);
  const [copying, setCopying]             = useState(false);
  const [confirm, ConfirmUI]              = useConfirm();
  const nav = useNavigate();
  const { getList } = useCustomLists();
  const EVENT_TYPES = getList("eventTypes");

  useEffect(() => {
    if (districtId) {
      setLoadingChurches(true);
      getChurches(districtId)
        .then((chs) => {
          setChurches(chs);
          if (chs.length) {
            let targetId = null;
            if (defaultChurchId) {
              targetId = chs.find((c) => String(c.id) === String(defaultChurchId))?.id;
            }
            if (!targetId) {
              try {
                const saved = sessionStorage.getItem(`agenda.church.${districtId}`);
                if (saved) targetId = chs.find((c) => String(c.id) === saved)?.id;
              } catch { /* ignore */ }
            }
            setSelectedChurch(targetId || chs[0].id);
          }
        })
        .finally(() => setLoadingChurches(false));
      getFieldEvents({ district_id: districtId, from_date: getLocalDateString() }).then(setFieldPool);
    }
    // eslint-disable-next-line
  }, [districtId, defaultChurchId]);

  useEffect(() => {
    if (selectedChurch && districtId) {
      try {
        sessionStorage.setItem(`agenda.church.${districtId}`, String(selectedChurch));
      } catch { /* ignore */ }
    }
  }, [selectedChurch, districtId]);

  useEffect(() => {
    if (selectedChurch) {
      setLoadingEvents(true);
      getChurchEvents({ church_id: selectedChurch })
        .then(setEvents)
        .finally(() => setLoadingEvents(false));
    }
  }, [selectedChurch]);

  const reload = async () => {
    if (!selectedChurch) return;
    try {
      setLoadingEvents(true);
      const list = await getChurchEvents({ church_id: selectedChurch });
      setEvents(list);
    } catch (err) {
      console.error("Reload local events error:", err);
    } finally {
      setLoadingEvents(false);
    }
  };

  const handleDelete = async (id) => {
    const ok = await confirm({
      title: "¿Eliminar este evento local?",
      description: "Esta acción no se puede deshacer.",
      confirmLabel: "Eliminar",
    });
    if (!ok) return;
    try {
      await deleteChurchEvent(id, { church_id: selectedChurch, district_id: districtId });
      toast.success("Evento eliminado correctamente");
      reload();
    } catch (e) {
      console.error("Delete local event error:", e?.response?.status, e?.response?.data);
      toast.error(e?.response?.data?.detail || "No se pudo eliminar el evento");
    }
  };

  const copyFromField = async (fe) => {
    if (!selectedChurch) { toast.error("Selecciona una iglesia primero"); return; }
    if (copying) return;
    setCopying(true);
    try {
      await createChurchEvent({
        church_id: selectedChurch,
        district_id: districtId,
        title: fe.title,
        date: fe.date,
        end_date: fe.end_date || null,
        time: fe.time || null,
        event_type: fe.event_type || "culto",
        notes: fe.notes || null,
      });
      await reload();
      toast.success("Evento copiado");
      setCopySheetOpen(false);
    } catch (e) {
      console.error("Copy field event error:", e);
      toast.error("No se pudo copiar el evento");
    } finally {
      setCopying(false);
    }
  };

  const selectedChurchObj = churches.find((c) => c.id === selectedChurch);
  const churchItems = churches.map((c) => ({ value: c.id, label: c.name }));

  const q = normalizeQ(searchQuery);
  const sortedEvents = useMemo(() => {
    const base = events.slice().sort(sortByEventDate);
    if (!q) return base;
    return base.filter((e) => {
      const typeLbl = EVENT_TYPES.find((t) => t.value === e.event_type)?.label || "";
      return (
        normalizeQ(e.title).includes(q) ||
        normalizeQ(e.notes).includes(q) ||
        normalizeQ(typeLbl).includes(q) ||
        normalizeQ(selectedChurchObj?.name).includes(q)
      );
    });
  }, [events, q, EVENT_TYPES, selectedChurchObj]);

  if (loadingChurches) {
    return (
      <div className="space-y-2">
        {[1, 2, 3].map((i) => (
          <div key={i} className="bg-white dark:bg-slate-800 rounded-[20px] h-[68px] animate-pulse border border-slate-100 dark:border-slate-700/60" />
        ))}
      </div>
    );
  }

  if (!loadingChurches && churches.length === 0) {
    return (
      <p className="text-center text-slate-400 dark:text-slate-500 text-[13px] py-8">
        Primero crea iglesias para ver su agenda local
      </p>
    );
  }

  return (
    <div className="space-y-3" data-testid="local-agenda">
      {ConfirmUI}

      {/* Copy from field modal */}
      <CenteredModal open={copySheetOpen} onClose={() => setCopySheetOpen(false)}>
        <div className="px-5 pt-5 pb-3 border-b border-slate-100 dark:border-slate-700/60">
          <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500 mb-0.5">
            Copiar desde el campo
          </p>
          <p className="text-[20px] font-bold text-slate-900 dark:text-slate-100 leading-none">
            Eventos del campo
          </p>
        </div>
        <div className="overflow-y-auto max-h-72 p-3">
          {fieldPool.length === 0 ? (
            <p className="text-center text-slate-400 dark:text-slate-500 text-[13px] py-6">
              No hay eventos del campo próximos
            </p>
          ) : (
            <div className="space-y-2">
              {fieldPool.map((fe) => {
                const copied = events.some(
                  (e) => e.title === fe.title && e.date === fe.date && e.time === fe.time
                );
                return (
                  <button
                    key={fe.id}
                    onClick={() => !copied && copyFromField(fe)}
                    disabled={copied || copying}
                    className={`w-full text-left px-4 py-3.5 rounded-[16px] transition-all duration-200 ${
                      copied
                        ? "bg-slate-50 dark:bg-slate-700/40 cursor-not-allowed"
                        : "bg-white dark:bg-slate-800 border border-slate-200/50 dark:border-slate-700/50 hover:bg-brand-light dark:hover:bg-brand/10 active:scale-[0.98]"
                    }`}
                  >
                    <p className={`font-semibold text-[13px] truncate ${copied ? "text-slate-400 dark:text-slate-500" : "text-slate-800 dark:text-slate-100"}`}>
                      {fe.title}
                    </p>
                    {fe.date && (
                      <p className="text-[11px] text-slate-400 dark:text-slate-500 mt-0.5">{fe.date}</p>
                    )}
                    {copied && (
                      <span className="text-[10px] text-emerald-600 dark:text-emerald-400 font-semibold mt-0.5 block">
                        ✔ Ya agregado
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </CenteredModal>

      {/* Church selector — hidden when lockedChurchId is provided (coordinator context) */}
      {!lockedChurchId && churches.length > 1 ? (
        <InlinePicker
          fullWidth
          icon={<Church size={16} />}
          triggerLabel="Iglesia"
          placeholder="Seleccionar iglesia…"
          items={churchItems}
          value={selectedChurch}
          onChange={setSelectedChurch}
          data-testid="church-selector-row"
        />
      ) : !lockedChurchId && (
        selectedChurchObj && (
          <div className="flex items-center gap-3 bg-white dark:bg-slate-800 rounded-[20px] px-4 py-3.5 border border-slate-200/50 dark:border-slate-700/50 shadow-[0_2px_12px_rgba(0,0,0,0.03)]">
            <div className="h-9 w-9 rounded-[12px] bg-brand-light dark:bg-brand/20 flex items-center justify-center shrink-0">
              <Church size={16} className="text-brand dark:text-blue-400" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] text-slate-400 dark:text-slate-500 font-medium uppercase tracking-wide">Iglesia</p>
              <p className="text-[14px] font-semibold text-slate-800 dark:text-slate-100 truncate">
                {selectedChurchObj.name}
              </p>
            </div>
          </div>
        )
      )}

      {/* Action buttons */}
      {selectedChurch && (
        <div className="grid grid-cols-2 gap-2">
          <Link
            to={`/churches/${selectedChurch}/events/new`}
            state={{ returnTo: "/agenda" }}
            className="flex items-center justify-center gap-2 bg-brand text-white rounded-[16px] h-11 font-semibold text-[13px] shadow-[0_4px_12px_rgba(27,79,156,0.2)] transition-all duration-200 ease-out active:scale-[0.98] active:opacity-80"
            data-testid="local-event-add"
          >
            <Plus size={15} /> Nuevo evento
          </Link>
          <button
            onClick={() => setCopySheetOpen(true)}
            disabled={fieldPool.length === 0}
            className={`flex items-center justify-center gap-2 rounded-[16px] h-11 font-semibold text-[13px] transition-all duration-200 ease-out active:scale-[0.98] active:opacity-80 ${
              fieldPool.length === 0
                ? "bg-slate-100 dark:bg-slate-700/50 text-slate-300 dark:text-slate-600 cursor-not-allowed"
                : "bg-brand-light dark:bg-brand/20 text-brand dark:text-blue-300 border border-brand/15 dark:border-brand/30"
            }`}
            data-testid="local-copy-from-field"
          >
            <Copy size={15} /> Copiar de campo
          </button>
        </div>
      )}

      <AgendaTimeline
        items={sortedEvents}
        emptyMessage={q ? "Sin resultados" : "Sin eventos de la iglesia"}
        emptyAction={!q && selectedChurch && (
          <Link
            to={`/churches/${selectedChurch}/events/new`}
            state={{ returnTo: "/agenda" }}
            className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-brand dark:text-blue-400 transition-all duration-150 active:opacity-70"
          >
            <Plus size={13} /> Agregar evento
          </Link>
        )}
        loading={loadingEvents}
        renderItem={(e) => (
          <AgendaEventCard
            key={e.id}
            href={`/churches/${selectedChurch}/events/${e.id}`}
            title={e.title}
            date={e.date}
            endDate={e.end_date}
            time={e.time}
            type="iglesia"
            typeLabel={EVENT_TYPES.find((t) => t.value === e.event_type)?.label}
            done={isEventDone(e)}
            onEdit={() => nav(`/churches/${selectedChurch}/events/${e.id}/edit`, { state: { returnTo: "/agenda" } })}
            onDelete={() => handleDelete(e.id)}
            testId={`local-event-${e.id}`}
            item={e}
          />
        )}
      />
    </div>
  );
}
